/**
 * History API Client
 * Communicates with the backend API for reading history management
 */

import { getInstance } from "@opencampus/ochub-utils";
import type { ArticleHistory, StoredArticleSummary } from "./wikipedia";

// API base URL - configurable via environment
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

interface StartReadingRequest {
  pageId: number;
  title: string;
  url: string;
  description?: string;
  categories: string[];
  thumbnail?: string;
}

interface MarkAsReadRequest {
  pageId: number;
  timeSpentSeconds: number;
}

interface MarkAsReadResponse {
  success: boolean;
  newMilestones: number[];
}

interface CurrentReadingResponse {
  current: StoredArticleSummary | null;
}

/**
 * Get authorization headers with Bearer token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const account = getInstance();
  const idToken = account.getIdToken();

  if (!idToken) {
    throw new Error("Not authenticated");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
  };
}

/**
 * Fetch user's reading history from the API
 */
export async function fetchHistory(): Promise<ArticleHistory> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/history`, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.status}`);
  }

  return response.json();
}

/**
 * Get current in-progress reading
 */
export async function fetchCurrentReading(): Promise<StoredArticleSummary | null> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/history/current`, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch current reading: ${response.status}`);
  }

  const data: CurrentReadingResponse = await response.json();
  return data.current;
}

/**
 * Start reading an article
 */
export async function startReading(
  article: StartReadingRequest,
): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/history/start`, {
    method: "POST",
    headers,
    body: JSON.stringify(article),
  });

  if (!response.ok) {
    throw new Error(`Failed to start reading: ${response.status}`);
  }
}

/**
 * Mark an article as read
 */
export async function markAsRead(
  data: MarkAsReadRequest,
): Promise<MarkAsReadResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/history/read`, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to mark as read: ${response.status}`);
  }

  return response.json();
}
